import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, UserCheck, UserX, Trash2, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/users/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPendingUsers(pendingRes.data.users);
      setAllUsers(allRes.data.users);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApprove = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User approved successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
    setConfirmDialog(null);
  };

  const handleReject = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User rejected');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
    setConfirmDialog(null);
  };

  const handleDelete = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
    setConfirmDialog(null);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'grower': return 'bg-green-100 text-green-800';
      case 'composting_supervisor': return 'bg-blue-100 text-blue-800';
      case 'growing_supervisor': return 'bg-cyan-100 text-cyan-800';
      case 'temperature_tech': return 'bg-orange-100 text-orange-800';
      case 'water_tech': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const formatRole = (role) => {
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading users...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Pending Approvals Alert */}
      {pendingUsers.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50" data-testid="pending-users-alert">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="w-5 h-5" />
              Pending Account Approvals ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-yellow-200"
                  data-testid={`pending-user-${user.id}`}
                >
                  <div>
                    <p className="font-medium text-gray-900">{user.full_name}</p>
                    <p className="text-sm text-gray-500">@{user.username}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {formatRole(user.role)}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {user.created_at && format(new Date(user.created_at), 'PPp')}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => setConfirmDialog({ type: 'reject', user })}
                      data-testid={`reject-btn-${user.id}`}
                    >
                      <UserX className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => setConfirmDialog({ type: 'approve', user })}
                      data-testid={`approve-btn-${user.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#1A4D2E]" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4 bg-gray-100">
              <TabsTrigger value="all">All Users ({allUsers.length})</TabsTrigger>
              <TabsTrigger value="approved">Approved ({allUsers.filter(u => u.status === 'approved' || !u.status).length})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({allUsers.filter(u => u.status === 'pending').length})</TabsTrigger>
              <TabsTrigger value="rejected">Rejected ({allUsers.filter(u => u.status === 'rejected').length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <UsersTable 
                users={allUsers} 
                getRoleBadgeColor={getRoleBadgeColor}
                getStatusBadge={getStatusBadge}
                formatRole={formatRole}
                onApprove={(user) => setConfirmDialog({ type: 'approve', user })}
                onReject={(user) => setConfirmDialog({ type: 'reject', user })}
                onDelete={(user) => setConfirmDialog({ type: 'delete', user })}
              />
            </TabsContent>

            <TabsContent value="approved">
              <UsersTable 
                users={allUsers.filter(u => u.status === 'approved' || !u.status)} 
                getRoleBadgeColor={getRoleBadgeColor}
                getStatusBadge={getStatusBadge}
                formatRole={formatRole}
                onDelete={(user) => setConfirmDialog({ type: 'delete', user })}
              />
            </TabsContent>

            <TabsContent value="pending">
              <UsersTable 
                users={allUsers.filter(u => u.status === 'pending')} 
                getRoleBadgeColor={getRoleBadgeColor}
                getStatusBadge={getStatusBadge}
                formatRole={formatRole}
                onApprove={(user) => setConfirmDialog({ type: 'approve', user })}
                onReject={(user) => setConfirmDialog({ type: 'reject', user })}
                onDelete={(user) => setConfirmDialog({ type: 'delete', user })}
              />
            </TabsContent>

            <TabsContent value="rejected">
              <UsersTable 
                users={allUsers.filter(u => u.status === 'rejected')} 
                getRoleBadgeColor={getRoleBadgeColor}
                getStatusBadge={getStatusBadge}
                formatRole={formatRole}
                onApprove={(user) => setConfirmDialog({ type: 'approve', user })}
                onDelete={(user) => setConfirmDialog({ type: 'delete', user })}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog open={true} onOpenChange={() => setConfirmDialog(null)}>
          <DialogContent className="sm:max-w-[400px]" data-testid="confirm-dialog">
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.type === 'approve' && 'Approve User'}
                {confirmDialog.type === 'reject' && 'Reject User'}
                {confirmDialog.type === 'delete' && 'Delete User'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-600">
                {confirmDialog.type === 'approve' && `Are you sure you want to approve ${confirmDialog.user.full_name}? They will be able to login.`}
                {confirmDialog.type === 'reject' && `Are you sure you want to reject ${confirmDialog.user.full_name}? They will not be able to login.`}
                {confirmDialog.type === 'delete' && `Are you sure you want to permanently delete ${confirmDialog.user.full_name}? This cannot be undone.`}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmDialog(null)}>
                Cancel
              </Button>
              <Button
                className={`flex-1 ${
                  confirmDialog.type === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  confirmDialog.type === 'reject' ? 'bg-orange-600 hover:bg-orange-700' :
                  'bg-red-600 hover:bg-red-700'
                } text-white`}
                onClick={() => {
                  if (confirmDialog.type === 'approve') handleApprove(confirmDialog.user.id);
                  else if (confirmDialog.type === 'reject') handleReject(confirmDialog.user.id);
                  else if (confirmDialog.type === 'delete') handleDelete(confirmDialog.user.id);
                }}
                data-testid="confirm-action-btn"
              >
                {confirmDialog.type === 'approve' && 'Approve'}
                {confirmDialog.type === 'reject' && 'Reject'}
                {confirmDialog.type === 'delete' && 'Delete'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Users Table Component
const UsersTable = ({ users, getRoleBadgeColor, getStatusBadge, formatRole, onApprove, onReject, onDelete }) => {
  if (users.length === 0) {
    return <div className="text-center py-8 text-gray-500">No users found</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">User</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Created</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4">
                <p className="font-medium text-gray-900">{user.full_name}</p>
                <p className="text-sm text-gray-500">@{user.username}</p>
              </td>
              <td className="py-3 px-4">
                <Badge className={getRoleBadgeColor(user.role)}>
                  {formatRole(user.role)}
                </Badge>
              </td>
              <td className="py-3 px-4">
                {getStatusBadge(user.status || 'approved')}
              </td>
              <td className="py-3 px-4 text-sm text-gray-500">
                {user.created_at ? format(new Date(user.created_at), 'PP') : '-'}
              </td>
              <td className="py-3 px-4 text-right">
                <div className="flex justify-end gap-1">
                  {user.status === 'pending' && onApprove && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-green-600 hover:bg-green-50"
                      onClick={() => onApprove(user)}
                    >
                      <UserCheck className="w-4 h-4" />
                    </Button>
                  )}
                  {user.status === 'pending' && onReject && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-orange-600 hover:bg-orange-50"
                      onClick={() => onReject(user)}
                    >
                      <UserX className="w-4 h-4" />
                    </Button>
                  )}
                  {user.status === 'rejected' && onApprove && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-green-600 hover:bg-green-50"
                      onClick={() => onApprove(user)}
                    >
                      <UserCheck className="w-4 h-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(user)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserManagement;
